import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CloseButton from 'Douleutaras/components/Buttons/CloseButton';
import ReactSplide from 'Douleutaras/components/Slider/ReactSplide';
import SliderMainItem from 'Douleutaras/components/Slider/parts/SliderMainItem';
import { TABLET_BIG } from 'Douleutaras/constants/media-queries';
import { SplideRef } from 'Douleutaras/types';
import LineClamp from 'Douleutaras/components/LineClamp/LineClamp';
import { isMobile } from 'react-device-detect';
import classNames from 'classnames';

import { SliderMediaType } from './types';
import SliderPreviewItem from './parts/SliderPreviewItem';
import './SliderGallery.scss';

interface SliderGalleryProps {
  openingIndex?: number;
  displayThumbs?: boolean;
  liteHeader?: boolean;
  slides: Array<{
    url: string,
    thumbUrl: string,
    caption: string,
    altImageText: string,
    type: SliderMediaType
  }>;
  dismissCallback(): void;
}

const CAPTION_CHAR_LIMIT_MOBILE = 75;
const CAPTION_CHAR_LIMIT_DESKTOP = 230;

const videoAutoPlay = ref => {
  setTimeout(() => {
    ref?.play();
  }, 0);
};

const SliderGallery = ({ openingIndex, slides, displayThumbs = true, liteHeader = false, dismissCallback }: SliderGalleryProps) => {
  const mainSliderRef = useRef<SplideRef>();
  const navigationSliderRef = useRef<SplideRef>();
  const [ slideIndex, setSlideIndex ] = useState(openingIndex + 1 || 0);
  const [ slideCaption, setSlideCaption ] = useState(slides[openingIndex]?.caption || '');
  const [ slideType, setSlideType ] = useState(slides[openingIndex]?.type || '');
  const videoRefs = useRef([]);
  const CAPTION_CHARS_LIMIT = isMobile ? CAPTION_CHAR_LIMIT_MOBILE : CAPTION_CHAR_LIMIT_DESKTOP;

  const stopAllVideos = () => {
    videoRefs.current.forEach(video => {
      if (video) {
        video.pause();
        video.currentTime = 0;
      }
    });
  };

  const moveToSlideIndex = async (index: number) => {
    mainSliderRef?.current?.splide.go(index);
    if (displayThumbs) {
      navigationSliderRef?.current?.splide.go(index);
    }
  };

  useEffect(() => {
    mainSliderRef?.current?.splide.on('move', (newIndex, prevIndex, destIndex) => {
      setSlideIndex(newIndex + 1);
      setSlideCaption(slides[newIndex].caption);
      setSlideType(slides[newIndex].type);
    });
    // Auto play when video animation is finished
    mainSliderRef?.current?.splide.on('moved', newIndex => {
      videoAutoPlay(videoRefs.current[newIndex]);
    });
    // There is no other way to start with a custom index, events or options aren't working
    setTimeout(() => {
        moveToSlideIndex(openingIndex);
        setSlideIndex(openingIndex + 1);
      }, 1);

    if (displayThumbs) {
      mainSliderRef?.current?.splide.sync(navigationSliderRef?.current?.splide);
    }

    // Auto play video on first index
    if (openingIndex === 0 && videoRefs.current[0] !== undefined) {
      videoAutoPlay(videoRefs.current[0]);
    }
  }, []);

  const { t } = useTranslation();

  return (
    <div styleName="modal-panel">
      <section styleName={classNames('modal-header', {
        'modal-header--lite-header': liteHeader
      })}>
        <div styleName="modal-header__index">
          {slides.length > 1 &&
            <>{slideIndex} / {slides.length}</>
          }
        </div>
        <div styleName="modal-header__close">
          <CloseButton onClick={dismissCallback} ariaLabel={t('Close')} options={{
            withShadow: false,
            inverseOnlyMobile: liteHeader,
            inverse: !liteHeader }}
          />
        </div>
      </section>
      <section styleName="main-file">
        { slideType !== SliderMediaType.VIDEO && slideCaption?.length >= 0 &&
          <aside styleName="main-file__index">
            <LineClamp
              maxChars={CAPTION_CHARS_LIMIT}
            >
              {slideCaption}
            </LineClamp>
          </aside>
        }
        <div styleName="main-file__gallery">
          <ReactSplide
            ref={mainSliderRef}
            innerArrows
            options={{
              height: '518px',
              arrows: true,
              focus: 0,
              trimSpace: false,
              perPage: 1,
              paginationKeyboard: true,
              keyboard: 'global',
              breakpoints: {
                [TABLET_BIG.MIN_WIDTH]: {
                  height: ''
                }
              }
            }}
          >
            {slides
              .filter(slide => Boolean(slide.url))
              .map((slide, i) => {
              return (
                <SliderMainItem caption={slide.caption} key={`slide_main_${slide.url}`}>
                  { slide.type === SliderMediaType.VIDEO
                    ? <video
                        playsInline
                        controls
                        autoPlay={false}
                        preload="metadata"
                        styleName="video-container"
                        ref={el => (videoRefs.current[i] = el)}
                    >
                        <source src={slide.url} />
                    </video>
                    : <img src={slide.url} alt={slide.altImageText}/>
                  }
                </SliderMainItem>
              );
            })}
          </ReactSplide>
        </div>
      </section>
      { displayThumbs &&
        <section styleName="file-gallery">
        <ReactSplide
          ref={navigationSliderRef}
          options={{
            fixedWidth : '80px',
            fixedHeight: '57px',
            gap: '10px',
            arrows: !isMobile,
            isNavigation: true,
            focus: 0,
            direction: 'ltr'
          }}
          onMoved={() => stopAllVideos()}
        >
          {slides.map((slide, i) => {
            return (
              <div styleName="slider-preview-item" key={`slide_main_${i}`}>
                <SliderPreviewItem type={slide.type} src={slide.thumbUrl} size="medium" />
              </div>
            );
          })}
        </ReactSplide>
      </section>
      }
    </div>
  );
};

export default SliderGallery;
